import { zodResolver } from "@hookform/resolvers/zod";
import { Turnstile } from "@marsidev/react-turnstile";
import { Link, useNavigate, useSearch } from "@tanstack/react-router";
import { ArrowLeftIcon } from "lucide-react";
import { useId, useState } from "react";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { cn } from "@/lib/utils";
import { resetPasswordRoute } from "@/routes/resetPasswordRoute";
import { authClient } from "../../lib/auth";
import { Field, FieldError, FieldGroup, FieldLabel } from "../ui/field";

// Zod validation schema
const resetPasswordSchema = z
    .object({
        password: z.string().min(1, "Password is required").min(8, "Password must be at least 8 characters long"),
        confirmPassword: z.string().min(1, "Please confirm your password"),
    })
    .refine((data) => data.password === data.confirmPassword, {
        message: "Passwords don't match",
        path: ["confirmPassword"],
    });

type ResetPasswordFormData = z.infer<typeof resetPasswordSchema>;

export function ResetPasswordForm({ className, ...props }: React.ComponentProps<"div">) {
    const [turnstileToken, setTurnstileToken] = useState("");
    const [isLoading, setIsLoading] = useState(false);
    const { requested, token } = useSearch({ from: "/reset-password" }) as {
        requested: string | undefined;
        token: string | undefined;
    };
    const navigate = useNavigate();

    const {
        register,
        handleSubmit,
        setError,
        formState: { errors },
    } = useForm<ResetPasswordFormData>({
        resolver: zodResolver(resetPasswordSchema),
        mode: "onBlur",
    });

    const onSubmit = async (values: ResetPasswordFormData) => {
        setIsLoading(true);
        const { data, error } = await authClient.resetPassword({
            newPassword: values.password,
            token: token || "",
            fetchOptions: {
                headers: {
                    "x-captcha-response": turnstileToken || "",
                },
            },
        });

        if (error) {
            console.error("Request error:", error);
            setError("password", { message: error.message });
        } else {
            console.log("Request successful:", data);
            navigate({
                to: resetPasswordRoute.to,
                search: { requested: "true" },
            });
        }
        setIsLoading(false);
    };

    const id = useId();

    if (requested) {
        return (
            <Card>
                <CardHeader className="text-center">
                    <CardTitle className="text-xl">Reset Password</CardTitle>
                </CardHeader>
                <CardContent>
                    <div className="mb-6">Your password has been reset successfully.</div>
                    <div>
                        <Button
                            nativeButton={false}
                            variant="default"
                            className="w-full text-base"
                            render={
                                <Link to="/login">
                                    <ArrowLeftIcon />
                                    Back to login
                                </Link>
                            }
                        ></Button>
                    </div>
                </CardContent>
            </Card>
        );
    }

    return (
        <div className={cn("flex flex-col gap-6", className)} {...props}>
            <Card>
                <CardHeader className="text-center">
                    <CardTitle className="text-xl">Reset Password</CardTitle>
                    <CardDescription>Enter your new password</CardDescription>
                </CardHeader>
                <CardContent>
                    <form onSubmit={handleSubmit(onSubmit)} id={id}>
                        <div className="grid gap-6">
                            <div className="grid gap-6">
                                <FieldGroup className="gap-4">
                                    <Field>
                                        <FieldLabel htmlFor="password">Password</FieldLabel>
                                        <Input type="password" {...register("password")} className={errors.password ? "border-red-500" : ""} />
                                        <FieldError errors={[errors.password]} />
                                    </Field>
                                    <Field>
                                        <FieldLabel htmlFor="confirmPassword">Confirm Password</FieldLabel>
                                        <Input
                                            type="password"
                                            {...register("confirmPassword")}
                                            className={errors.confirmPassword ? "border-red-500" : ""}
                                        />
                                        <FieldError errors={[errors.confirmPassword]} />
                                    </Field>
                                </FieldGroup>

                                <Turnstile
                                    onSuccess={setTurnstileToken}
                                    className="w-full mx-auto"
                                    options={{
                                        theme: "light",
                                        size: "flexible",
                                    }}
                                    siteKey={import.meta.env.VITE_TURNSTILE_SITE_KEY!}
                                />
                                <Button type="submit" form={id} className="w-full [&_iframe]:mx-auto" disabled={isLoading || !token}>
                                    {isLoading ? "Resetting password..." : "Reset password"}
                                </Button>
                            </div>
                            <div className="text-center ">
                                Remember your password?{" "}
                                <a href="/login" className="underline underline-offset-4">
                                    Login
                                </a>
                            </div>
                        </div>
                    </form>
                </CardContent>
            </Card>
        </div>
    );
}
